'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface ErrorBoundaryProps {
  children: ReactNode
  /** Rendered when an error is caught. Receives the error and a reset callback. */
  fallback?: (props: { error: Error; reset: () => void }) => ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * Shared error boundary for wrapping individual page sections.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary fallback={({ error, reset }) => <p>Oops: {error.message} <button onClick={reset}>Retry</button></p>}>
 *   <RiskyComponent />
 * </ErrorBoundary>
 * ```
 *
 * If no `fallback` is provided a minimal default is rendered.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  reset = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    const { children, fallback } = this.props

    if (error) {
      if (fallback) return fallback({ error, reset: this.reset })

      return (
        <div
          role="alert"
          style={{
            padding: '16px 20px',
            borderRadius: '12px',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            background: 'rgba(239, 68, 68, 0.06)',
            fontSize: '14px',
            lineHeight: '1.5',
          }}
        >
          <p style={{ fontWeight: 600, color: '#991b1b' }}>Something went wrong</p>
          <p style={{ marginTop: '4px', color: '#b91c1c' }}>{error.message}</p>
          <button
            type="button"
            onClick={this.reset}
            style={{
              marginTop: '8px',
              padding: '6px 14px',
              borderRadius: '8px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#991b1b',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      )
    }

    return children
  }
}
